/**
 * Single source of truth for UI glyph paths (24×24 stroked SVG `d` data).
 *
 * Replaces the ~12 near-identical `export const I = {…}` maps that used to live
 * in each screen's data.ts. Where the old maps reused one key for two different
 * glyphs (e.g. `spark` meant a wand on some screens, a star on others), the
 * distinct glyphs are kept under distinct keys here and the screens reference
 * the one they actually rendered — so appearance is preserved.
 *
 * App/connector glyphs live separately in catalog.ts (`ICON_SET`), keyed by the
 * names `AppDef.icon` references and served on `Catalog.iconSet`.
 */
export const ICONS = {
    // navigation / chrome
    home: 'M3 11l9-8 9 8 M5 10v10h14V10 M9 20v-6h6v6',
    grid: 'M3 3h7v7H3z M14 3h7v7h-7z M14 14h7v7h-7z M3 14h7v7H3z',
    teams: 'M17 20v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M9 10a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M23 20v-2a4 4 0 0 0-3-3.9 M16 2.1a4 4 0 0 1 0 7.8',
    monitor: 'M3 3v18h18 M8 17v-4 M13 17V8 M18 17v-7',
    signals: 'M22 12h-4l-3 9L9 3l-3 9H2',
    inbox: 'M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9z M13.7 21a2 2 0 0 1-3.4 0',
    // arrows / chevrons
    arrow: 'M9 6l6 6-6 6',
    back: 'M15 6l-6 6 6 6',
    chev: 'M9 6l6 6-6 6',
    chevSm: 'M13 6l6 6-6 6',
    chevDown: 'M6 9l6 6 6-6',
    up: 'M12 19V5 M6 11l6-6 6 6',
    down: 'M12 5v14 M6 13l6 6 6-6',
    // status / feedback
    alert: 'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z M12 9v4 M12 17h0',
    warn: 'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z M12 9v4 M12 17h0',
    alertc: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 8v5 M12 16h0',
    check: 'M20 6 9 17l-5-5',
    checkc: 'M20 6 9 17l-5-5',
    x: 'M6 6l12 12 M18 6 6 18',
    bell: 'M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9z M13.7 21a2 2 0 0 1-3.4 0',
    // actions
    plus: 'M12 5v14 M5 12h14',
    edit: 'M12 20h9 M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z',
    pen: 'M12 20h9 M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z',
    trash: 'M3 6h18 M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2 M19 6l-1 14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1L5 6',
    search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z M21 21l-4-4',
    send: 'M22 2 11 13 M22 2l-7 20-4-9-9-4 20-7z',
    refresh: 'M3 12a9 9 0 0 1 15-6.7L21 8 M21 3v5h-5 M21 12a9 9 0 0 1-15 6.7L3 16 M3 21v-5h5',
    retry: 'M23 4v6h-6 M1 20v-6h6 M3.51 9a9 9 0 0 1 14.85-3.36L23 10 M1 14l4.64 4.36A9 9 0 0 0 20.49 15',
    restore: 'M3 12a9 9 0 1 0 3-6.7L3 8 M3 3v5h5',
    upload: 'M12 16V4 M6 10l6-6 6 6 M4 20h16',
    play: 'M5 3l16 9-16 9z',
    pause: 'M6 4h4v16H6z M14 4h4v16h-4z',
    link: 'M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-2 2 M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l2-2',
    eye: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
    lock: 'M5 11h14v10H5z M8 11V7a4 4 0 0 1 8 0v4',
    // objects / domain
    bot: 'M12 2v2 M5 9h14a1 1 0 0 1 1 1v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a1 1 0 0 1 1-1z M9 14h.01 M15 14h.01 M12 4a1 1 0 1 0 0 2 1 1 0 0 0 0-2z',
    flow: 'M5 3h6v6H5z M5 15h6v6H5z M15 9h6v6h-6z M11 6h2a2 2 0 0 1 2 2v.5 M11 18h2a2 2 0 0 0 2-2v-.5',
    process: 'M5 3h6v6H5z M5 15h6v6H5z M15 9h6v6h-6z M11 6h2a2 2 0 0 1 2 2v.5 M11 18h2a2 2 0 0 0 2-2v-.5',
    branch: 'M6 3v12 M18 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M15 6a9 9 0 0 1-9 9',
    doc: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z M14 3v6h6 M9 13h6 M9 17h4',
    file: 'M6 2h9l5 5v15H6z M15 2v5h5 M9 13h6 M9 17h4',
    folder: 'M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
    book: 'M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2z M4 5v16',
    badge: 'M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2 M9 3h6v3H9z M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
    ledger: 'M4 4h12l4 4v12H4z M8 9h6 M8 13h8 M8 17h8',
    box: 'M21 8l-9-5-9 5v8l9 5 9-5z M3 8l9 5 9-5 M12 13v9',
    laptop: 'M4 5h16a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z M2 20h20',
    display: 'M3 4h18a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z M8 21h8 M12 17v4',
    keyboard: 'M3 7h18a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1z M7 11h.01 M11 11h.01 M15 11h.01 M8 14h8',
    phone: 'M7 2h10a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z M11 18h2',
    cart: 'M6 6h15l-1.5 9h-12z M6 6 5 2H2 M9 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2z M18 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2z',
    key: 'M15 7a4 4 0 1 1-3.9 5H8v3H5v3H2v-3l6.1-6A4 4 0 0 1 15 7z',
    tag: 'M20.6 13.4 11 3.8A2 2 0 0 0 9.6 3H4a1 1 0 0 0-1 1v5.6A2 2 0 0 0 3.4 11l9.6 9.6a2 2 0 0 0 2.8 0l4.6-4.6a2 2 0 0 0 0-2.8z M7.5 7.5h.01',
    flag: 'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z M4 22V15',
    cal: 'M3 4h18v18H3z M3 9h18 M8 2v4 M16 2v4',
    clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 7v5l3 2',
    alarm: 'M12 21a8 8 0 1 0 0-16 8 8 0 0 0 0 16z M12 9v4l2 2 M5 3 2 6 M22 6l-3-3',
    scale: 'M12 3v18 M5 7h14 M5 7 3 13a3 3 0 0 0 6 0z M19 7l-2 6a3 3 0 0 0 6 0z',
    // people
    id: 'M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M10 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
    user: 'M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M10 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
    userCircle: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M4 21c0-4 3.6-7 8-7s8 3 8 7',
    users: 'M17 21v-2a4 4 0 0 0-3-3.87 M9 21v-2a4 4 0 0 1 3-3.87 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M16 3.1a4 4 0 0 1 0 7.75',
    userplus: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M19 8v6 M22 11h-6',
    // shapes used as agent / accent avatars
    compass: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M16 8l-2.5 5.5L8 16l2.5-5.5z',
    target: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2z',
    shield: 'M12 3l8 3v6c0 5-8 9-8 9s-8-4-8-9V6l8-3z M9 12l2 2 4-4',
    shieldPlain: 'M12 3l8 3v6c0 5-8 9-8 9s-8-4-8-9V6l8-3z',
    map: 'M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2z M9 4v14 M15 6v14',
    headset: 'M4 14v-2a8 8 0 0 1 16 0v2 M4 14a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2z M20 14a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2',
    globe: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M3 12h18 M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18z',
    // sparkles / energy (kept distinct — these were one colliding key across screens)
    spark: 'M4.5 19.5 13 11 M16 3.5l1 2.9 2.9 1-2.9 1-1 2.9-1-2.9-2.9-1 2.9-1z',
    wand: 'M4.5 19.5 13 11 M16 3.5l1 2.9 2.9 1-2.9 1-1 2.9-1-2.9-2.9-1 2.9-1z',
    sparkStar: 'M12 3l1.7 4.7L18.5 9l-4.8 1.3L12 15l-1.7-4.7L5.5 9l4.8-1.3L12 3z',
    star: 'M12 3l2.6 6.6L21 10.3l-5 4.3 1.6 6.4L12 17.8 6.4 21l1.6-6.4-5-4.3 6.4-.7z',
    bolt: 'M13 2L4 14h7l-1 8 9-12h-7l1-8z',
    bulb: 'M9 18h6 M10 22h4 M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1h6c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2z',
    brain: 'M12 5a3 3 0 0 0-3 3 3 3 0 0 0-3 3 3 3 0 0 0 0 6 3 3 0 0 0 3 1 3 3 0 0 0 6 0 3 3 0 0 0 3-1 3 3 0 0 0 0-6 3 3 0 0 0-3-3 3 3 0 0 0-3-3z',
    // charts / analytics (bar vs line were one colliding key)
    chart: 'M3 3v18h18 M8 17v-5 M13 17V8 M18 17v-9',
    lineChart: 'M3 3v18h18 M7 15l3-4 3 3 4-6',
    bars: 'M3 3v18h18 M8 17v-5 M13 17V8 M18 17v-9',
    stack: 'M3 12h4l2 6 4-14 2 8h6',
    trend: 'M3 17l6-6 4 4 8-8 M21 7v6h-6',
    gauge: 'M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M13.4 12.6 19 7 M4 18a9 9 0 1 1 16 0',
    threshold: 'M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M13.4 12.6 19 7 M4 18a9 9 0 1 1 16 0',
    radar: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M12 12 19 5',
    // controls / misc (two slider styles were one colliding key)
    sliders: 'M3 5h18 M7 12h10 M11 19h6',
    slidersDetailed: 'M4 6h11 M19 6h1 M4 12h1 M9 12h11 M4 18h7 M15 18h5 M15 4v4 M5 10v4 M11 16v4',
    list: 'M8 6h13 M8 12h13 M8 18h13 M3 6h.01 M3 12h.01 M3 18h.01',
    memory: 'M9 3v2 M15 3v2 M9 19v2 M15 19v2 M3 9h2 M3 15h2 M19 9h2 M19 15h2 M6 6h12v12H6z M9 9h6v6H9z',
    cap: 'M22 10 12 5 2 10l10 5 10-5z M6 12v5c0 2.2 2.7 3.5 6 3.5s6-1.3 6-3.5v-5',
    beaker: 'M9 2v6 M15 2v6 M6 8h12v3a6 6 0 0 1-12 0z M12 17v5',
    app: 'M9 2v6 M15 2v6 M6 8h12v3a6 6 0 0 1-12 0z M12 17v5',
    data: 'M9 3h6 M10 3v6L4 19a2 2 0 0 0 1.8 3h12.4A2 2 0 0 0 20 19l-6-10V3 M7 15h10',
    db: 'M12 8a8 3 0 1 0 0-6 8 3 0 0 0 0 6z M4 5v6a8 3 0 0 0 16 0V5 M4 11v6a8 3 0 0 0 16 0v-6',
    dbAlt: 'M4 7c0-1.7 3.6-3 8-3s8 1.3 8 3-3.6 3-8 3-8-1.3-8-3z M4 7v10c0 1.7 3.6 3 8 3s8-1.3 8-3V7 M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3',
    // channels / brands
    slack: 'M4 9h16 M4 15h16 M10 3 8 21 M16 3l-2 18',
    mail: 'M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z M3 7l9 6 9-6',
    chat: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
    code: 'M8 6l-6 6 6 6 M16 6l6 6-6 6',
    github: 'M9 19c-5 1.5-5-2.5-7-3 M15 22v-3.9a3.4 3.4 0 0 0-1-2.6c3.3-.4 6.7-1.6 6.7-7A5.4 5.4 0 0 0 19 4.8 5 5 0 0 0 18.9 1S17.7.7 15 2.5a13 13 0 0 0-7 0C5.3.7 4.1 1 4.1 1A5 5 0 0 0 4 4.8 5.4 5.4 0 0 0 2.5 8.5c0 5.4 3.4 6.6 6.7 7a3.4 3.4 0 0 0-1 2.6V22',
};
/** Resolve an icon key to its SVG path data. Falls back to the `bot` glyph. */
export function iconPath(key) {
    return ICONS[key] ?? ICONS.bot;
}
//# sourceMappingURL=icons.js.map